// File: typescript/apps/front-end/src/main/react/footer/FooterCookieSettings.tsx
//
// The footer's "Cookie settings" entry — the consent-withdrawal control the cookie policy and the
// privacy policy both tell the visitor to use ("you can withdraw your consent at any time via the
// Cookie settings link in the website footer"). Until 2026-08-14 that link did not exist, so for an
// EEA/UK/Swiss visitor the documents described a mechanism the product did not offer
// (ux-critique POLICY-CONTACT-3).
//
// WHY AN ISLAND AND NOT A JTE <a>. Two independent reasons, both settled in
// designs/07-archive/runs/2026-08-14-ux-critique-remediation-wave-2/batch-5-footer-cookie-settings.md:
//   1. it has to call JS (`openCookieSettings`) and be gated on a client-side availability read
//      (`isCookieConsentAvailable()` reads a meta tag); an href can do neither, and there is no
//      onclick= or data-* -> addEventListener convention anywhere in MainService/src/main/jte/;
//   2. the BACKEND catalogs have no consent key at all, so a server-rendered label would mint copy
//      across 24 locales (rule 11). The FRONTEND catalog already carries `sidebar.cookie-settings`
//      in all 25 files, so this route costs zero new copy. That is the whole reason it was chosen.
//
// The sidebar's equivalent control (MenuSideBarView.tsx `SidebarLegalLinks`) is the precedent this
// copies — same gate, same handler, same key. It keeps its own `data-playwright-id`; this one has its
// own so a spec can tell the two apart.

import React from "react";

import { isCookieConsentAvailable, openCookieSettings } from "@/main/analytics/consent/cookieConsent";
import { createReactMount } from "@/main/react/mountContract";
import { Localizer } from "@/main/react/Types";

// Same treatment as the four <a>s beside it — site-footer.jte hoists this exact string as `linkClass`.
// `text-neutral!` is load-bearing there because of the app's unlayered `a { color: var(--text-main) }`;
// it is kept here so the button cannot drift away from its siblings if that rule ever widens.
// `text-left` + `cursor-pointer` are the two places a <button> does not already match an <a>: Tailwind
// v4's preflight centers button text and gives it `cursor: default`.
//
// THE COARSE-POINTER FLOOR MOVES WITH THE SIBLINGS, and here that is not tidiness — it is the specific
// mistap ux-critique POLICY-CONTACT-28 names as the worst one. This control sits directly beneath
// "Cookie Policy" at a 28 CSS px pitch on a phone, and the two do completely different things: one
// navigates to a document, this one opens the consent dialog both policy documents instruct EEA/UK
// visitors to use to withdraw consent. Leaving this button at 20px while the anchors above it grow to
// 44 would make it the only undersized target in the column, which is worse than the state the row was
// filed against. `pointer-coarse:` gates on the input device, so a mouse sees no change at all.
const FOOTER_LINK_CLASS =
    "text-neutral-text! standard-hover rounded-sm outline-none focus-visible:border-ring " +
    "focus-visible:ring-ring/70 focus-visible:ring-[3px] text-left cursor-pointer " +
    "pointer-coarse:flex pointer-coarse:min-h-11 pointer-coarse:items-center";

/**
 * A95 (ux-critique POLICY-CONTACT-59, 2026-09-28): the cookie policy's "Your Consent and Choices" section
 * tells the reader to use this control, which lived only in the footer, a long scroll below. `policy.jte`
 * now mounts the SAME island after the document body on the cookie policy only (`data-placement="policy"`),
 * where it renders as a visible outline pill with its own hook. Same gate, same handler, same key.
 * Round 66 D4 moves that mount, client-side, right after the paragraph naming the control
 * ({@link placePolicyCookieSettingsMount}).
 */
const POLICY_PLACEMENT_CLASS =
    "inline-flex min-h-11 items-center rounded-full border border-control-boundary px-5 text-sm font-medium " +
    "standard-hover outline-none focus-visible:border-ring focus-visible:ring-ring/70 focus-visible:ring-[3px] cursor-pointer";

export type CookieSettingsPlacement = "footer" | "policy";

export type FooterCookieSettingsProps = {
    /**
     * Whether the consent layer is running for this visitor. Read by the MOUNT (see
     * {@link mountFooterCookieSettings}), not by this component — so that a throw from the meta read
     * cannot reach React and turn an optional footer control into an error card.
     */
    available: boolean;
    localizer: Localizer;
    /** Where the island is mounted. Defaults to the footer, the only placement before A95. */
    placement?: CookieSettingsPlacement;
};

/**
 * Renders nothing when the consent layer is not active — the same gate the sidebar applies. Without
 * a banner there are no preferences to reopen (`openCookieSettings` would no-op), and a control that
 * does nothing is worse than an absent one.
 */
export function FooterCookieSettings({ available, localizer, placement = "footer" }: FooterCookieSettingsProps): React.ReactElement | null {
    if (!available) {
        return null;
    }

    if (placement === "policy") {
        return (
            <div className="mb-4">
                <button
                    type="button"
                    data-playwright-id="policy-cookie-settings"
                    onClick={openCookieSettings}
                    className={POLICY_PLACEMENT_CLASS}
                >
                    {localizer("sidebar.cookie-settings")}
                </button>
            </div>
        );
    }

    return (
        <button
            type="button"
            data-playwright-id="footer-cookie-settings"
            onClick={openCookieSettings}
            className={FOOTER_LINK_CLASS}
        >
            {localizer("sidebar.cookie-settings")}
        </button>
    );
}

/**
 * Rule 32 — this island may only ADD to the footer, never subtract from it.
 *
 * <p>Its container is empty server-side, so `createRoot()` has nothing to discard and there is no
 * SSR snapshot to lose. What remains is making sure a failure renders NOTHING rather than something
 * broken, and the availability read is where that is decided: done here, inside `createReactMount`'s
 * try/catch, a throw is logged and the container is left exactly as the server sent it — empty.
 * Done during render it would reach `MountErrorBoundary`, which for a blank container renders the
 * `RecoverableIslandFallback` "Something went wrong" card — a `role="alert"` block several times the
 * height of the footer column it would sit in.
 *
 * <p>Registered in `ReactMounter`'s STATIC map rather than the lazy one, so there is no chunk and
 * therefore no chunk-failure branch either. The consent module is already eager
 * (`bootstrapFrontEnd.ts` imports it at startup), so a lazy chunk would buy a per-page round-trip
 * for code main_bundle already contains, plus that same fallback card as its failure mode.
 */
/**
 * D4 (board settle round 66, POLICY-CONTACT-59): on the cookie policy the pill belongs beside the sentence
 * that tells the reader to use it ("You can change or withdraw your choice at any time using the
 * "Cookie settings" link..."), not after the whole document, a long phone scroll below that section.
 * The stored policy text and `policy.jte` are unchanged (no policy version, no new copy): the client finds
 * the first paragraph of the rendered document whose text contains the pill's OWN localized label and moves
 * the island's EMPTY container to right after it. When no paragraph matches (a locale whose policy text
 * words the control differently), the container stays where the server put it, after the document.
 *
 * Rule 32: only the island's own empty container moves; no server-rendered node is removed or changed.
 * Returns whether the container moved.
 */
export function placePolicyCookieSettingsMount(container: HTMLElement, label: string): boolean {
    const needle = label.trim().toLocaleLowerCase();
    if (needle.length === 0) {
        return false;
    }
    const policyDocument = container.ownerDocument.querySelector(".policy-document");
    if (!policyDocument) {
        return false;
    }
    const paragraph = Array.from<HTMLParagraphElement>(policyDocument.querySelectorAll("p")).find(
        (candidate) => (candidate.textContent ?? "").toLocaleLowerCase().includes(needle),
    );
    if (!paragraph) {
        return false;
    }
    paragraph.insertAdjacentElement("afterend", container);
    return true;
}

export const mountFooterCookieSettings = createReactMount<{ available: boolean; placement: CookieSettingsPlacement }>({
    name: "mountFooterCookieSettings",
    getProps: ({ parsed, element, localizer }) => {
        const available = isCookieConsentAvailable();
        const placement: CookieSettingsPlacement = parsed.placement === "policy" ? "policy" : "footer";
        // Only a pill that will render is moved: with the consent layer off the container stays empty
        // wherever the server put it.
        if (available && placement === "policy") {
            placePolicyCookieSettingsMount(element, localizer("sidebar.cookie-settings"));
        }
        return { available, placement };
    },
    render: ({ available, placement }, { localizer }) => (
        <FooterCookieSettings available={available} localizer={localizer} placement={placement} />
    ),
});
